import { RootState } from 'app'
import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import qs from 'querystring'
import { MODULE_NAME } from 'modules/Lawsuits/strings'
import { downloadFile, getNonEmptyValuesObject } from 'shared/utils'
import Api, { paths } from 'shared/services/Api'
import { selectors as commonSelectors } from 'shared/store'
import {
  LawsuitCircumstanceQuestion,
  LoadingStatus,
  TableSorting,
  NavigationItemStatus,
  BatchApiErrorElement,
  LawsuitDetails,
  LawsuitLinks,
  BankTransfer,
  Message,
} from 'shared/types'
import {
  getSorting,
  getPowerOfAttorneyTemplateState,
  getCircumstanceQuestionsState,
  getPartiesFormValues,
  getDocumentsState,
  getCircumstancesFormValues,
  getDataCorrectionFiles,
  getFilesAndAnswersToCorrect,
  getFiledLawsuitFiles,
  getUpdateLawsuitFiles,
  getSendMessageFiles,
} from './selectors'
import {
  GetRecommendedInvoicesPayload,
  GetRecommendedInvoicesResponse,
  GetLawsuitsResponse,
  GetLawyerLawsuitsResponse,
  VerifyLawsuitDataPayload,
  SetBankTransferInfoPayload,
} from './actions.types'
import {
  CircumstancesFormValues,
  PartiesFormValues,
  VerificationFormValues,
  LawsuitCorrectionFormValues,
} from '../types'

export const getLawsuits = createAsyncThunk<
  GetLawsuitsResponse,
  void,
  { rejectValue: string }
>(`${MODULE_NAME}/getLawsuits`, async (_, { rejectWithValue }) => {
  try {
    const response = await Api.get(`${paths.Lawsuits.root}`)
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const getRecommendedInvoices = createAsyncThunk<
  GetRecommendedInvoicesResponse,
  GetRecommendedInvoicesPayload | undefined,
  { rejectValue: string }
>(
  `${MODULE_NAME}/getRecommendedInvoices`,
  async (params, { rejectWithValue, getState }) => {
    try {
      const sorting: TableSorting = getSorting(getState() as RootState)
      const response = await Api.get(paths.Invoices.root, {
        params: {
          ...params,
          ...sorting,
          onlySuitableForLawsuits: true,
        },
        paramsSerializer: params => qs.stringify(params),
      })
      return {
        rows: response.data.results,
        totalCount: response.data.totalRowsCount,
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getPowerOfAttorneyTemplate = createAsyncThunk<
  Blob,
  { disableRefetch?: boolean } | undefined,
  { rejectValue: string }
>(
  `${MODULE_NAME}/getPowerOfAttorneyTemplate`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.get<string>(
        paths.Files.powerOfAttorneyTemplate,
        {
          responseType: 'blob',
        }
      )
      return new Blob([response.data])
    } catch (error) {
      return rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getPowerOfAttorneyTemplateState(
        getState() as RootState
      )
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const uploadPowerOfAttorney = createAsyncThunk<
  void,
  { file: File },
  { rejectValue: { error: string; errorCode: string } }
>(`${MODULE_NAME}/uploadPowerOfAttorney`, async ({ file }, thunkAPI) => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    await Api.post(`${paths.Users.powerOfAttorney}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getCircumstanceQuestions = createAsyncThunk<
  LawsuitCircumstanceQuestion[],
  { disableRefetch?: boolean } | undefined,
  { rejectValue: string }
>(
  `${MODULE_NAME}/getCircumstanceQuestions`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.get(paths.Lawsuits.questions)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
  {
    condition: (options, { getState }) => {
      const { loading } = getCircumstanceQuestionsState(getState() as RootState)
      if (
        (loading === LoadingStatus.Succeeded ||
          loading === LoadingStatus.Pending) &&
        options?.disableRefetch
      ) {
        return false
      }
    },
  }
)

export const fileLawsuit = createAsyncThunk<
  void,
  undefined,
  { rejectValue: BatchApiErrorElement[] }
>(`${MODULE_NAME}/fileLawsuit`, async (_, { rejectWithValue, getState }) => {
  const state = getState() as RootState
  const { bankAccountNumber: accountNumber, ...partiesFormValues } =
    getPartiesFormValues(state)
  const bankAccountNumber = ` ${accountNumber.split(' ')[1]}`
  const activeInvoice = commonSelectors.invoices.getActiveInvoice(state)
  const { additionalFiles, invoiceFiles } = getDocumentsState(state)
  const circumstances = getNonEmptyValuesObject(
    getCircumstancesFormValues(state)
  )

  const formData = new FormData()

  const data = {
    invoiceId: activeInvoice?.id || '',
    invoiceFiles,
    additionalFiles,
    bankAccountNumber,
    ...partiesFormValues,
  }
  const ANSWERS_KEY = 'answers'
  Object.entries(circumstances).forEach(([questionId, content], idx) => {
    formData.append(`${ANSWERS_KEY}.index`, `${idx}`)
    formData.append(`${ANSWERS_KEY}[${idx}].questionId`, `${questionId}`)
    formData.append(`${ANSWERS_KEY}[${idx}].content`, `${content}`)
  })

  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      return value.forEach(file => {
        formData.append(key, file)
      })
    }
    formData.append(key, `${value}`)
  })

  try {
    await Api.post(`${paths.Lawsuits.root}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  } catch (error) {
    return rejectWithValue(Array.isArray(error) ? error : [])
  }
})

export const setSorting = createAction<TableSorting>(
  `${MODULE_NAME}/setSorting`
)

export const clearUploadPowerOfAttorneyState = createAction(
  `${MODULE_NAME}/clearUploadPowerOfAttorney`
)

export const resetLawsuitFillingProgress = createAction(
  `${MODULE_NAME}/resetLawsuitFillingProgress`
)

export const setCircumstancesFormValues = createAction<CircumstancesFormValues>(
  `${MODULE_NAME}/setCircumstancesFormValues`
)

export const setCircumstancesStatus = createAction<NavigationItemStatus>(
  `${MODULE_NAME}/setCircumstancesStatus`
)

export const setInvoiceFiles = createAction<File[]>(
  `${MODULE_NAME}/setInvoiceFiles`
)

export const setAdditionalFiles = createAction<File[]>(
  `${MODULE_NAME}/setAdditionalFiles`
)

export const setDocumentsStatus = createAction<NavigationItemStatus>(
  `${MODULE_NAME}/setDocumentsStatus`
)

export const setPartiesStatus = createAction<NavigationItemStatus>(
  `${MODULE_NAME}/setPartiesStatus`
)

export const setPartiesFormValues = createAction<PartiesFormValues>(
  `${MODULE_NAME}/setPartiesFormValues`
)

export const setSummaryStatus = createAction<NavigationItemStatus>(
  `${MODULE_NAME}/setSummaryStatus`
)

export const toggleDocumentConsent = createAction(
  `${MODULE_NAME}/toggleDocumentConsent`
)

export const setVerificationFormValues = createAction<VerificationFormValues>(
  `${MODULE_NAME}/setVerificationFormValues`
)

export const addDataCorrectionFile = createAction<{
  fileId: number
  file: File
}>(`${MODULE_NAME}/addDataCorrectionFile`)

export const removeDataCorrectionFile = createAction<number>(
  `${MODULE_NAME}/removeDataCorrectionFile`
)

export const resetLawsuitVerificationLoadingStatus = createAction(
  `${MODULE_NAME}/resetLawsuitVerificationLoadingStatus`
)

export const resetBankTransferInfoSettingLoadingStatus = createAction(
  `${MODULE_NAME}/resetBankTransferInfoSettingLoadingStatus`
)

export const resetLawsuitDetails = createAction(
  `${MODULE_NAME}/resetLawsuitDetails`
)

export const clearLawsuitProcessErrors = createAction(
  `${MODULE_NAME}/clearLawsuitProcessErrors`
)

export const setFiledLawsuitFiles = createAction<File[]>(
  `${MODULE_NAME}/setFiledLawsuitFiles`
)

export const setUpdateLawsuitFiles = createAction<File[]>(
  `${MODULE_NAME}/setUpdateLawsuitFiles`
)

export const setSendMessageFiles = createAction<File[]>(
  `${MODULE_NAME}/setSendMessageFiles`
)

export const clearSendMessageErrors = createAction(
  `${MODULE_NAME}/clearSendMessageErrors`
)

export const clearLawsuitWithdrawState = createAction(
  `${MODULE_NAME}/clearLawsuitWithdrawState`
)

export const clearSendLawsuitWithdrawalMessageState = createAction(
  `${MODULE_NAME}/clearSendLawsuitWithdrawalMessageState`
)

export const getLawyerLawsuits = createAsyncThunk<
  GetLawyerLawsuitsResponse,
  Record<string, any>,
  { rejectValue: string }
>(`${MODULE_NAME}/getLawyerLawsuits`, async (filters, { rejectWithValue }) => {
  try {
    const response = await Api.get(`${paths.Lawsuits.lawyers.root}`, {
      params: filters,
    })
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const getLawsuitDetails = createAsyncThunk<
  LawsuitDetails,
  string,
  { rejectValue: string }
>(`${MODULE_NAME}/getLawsuitDetails`, async (id, { rejectWithValue }) => {
  try {
    const response = await Api.get(`${paths.Lawsuits.root}/${id}`)
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const downloadLawsuitFile = createAsyncThunk<
  void,
  { fileId: number; fileName: string; openInNewWindow?: boolean },
  { rejectValue: string }
>(
  `${MODULE_NAME}/downloadLawsuitFile`,
  async ({ fileId, fileName, openInNewWindow }, { rejectWithValue }) => {
    try {
      const response = await Api.get<string>(`${paths.Files.root}/${fileId}`, {
        responseType: 'blob',
      })
      const blob = response.data
      if (openInNewWindow) {
        const href = window.URL.createObjectURL(blob)
        window.open(href, '_blank')
      } else {
        downloadFile(blob, fileName)
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getLawsuitMessages = createAsyncThunk<
  Message[],
  { lawsuitId: string },
  { rejectValue: string }
>(
  `${MODULE_NAME}/getLawsuitMessages`,
  async ({ lawsuitId }, { rejectWithValue }) => {
    try {
      const response = await Api.get(
        `${paths.Lawsuits.root}/${lawsuitId}/messages`
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const verifyLawsuitData = createAsyncThunk<
  void,
  VerifyLawsuitDataPayload,
  { rejectValue: string }
>(`${MODULE_NAME}/verifyLawsuitData`, async (data, { rejectWithValue }) => {
  try {
    await Api.put(`${paths.Lawsuits.root}/${data.id}/assess`, data)
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const correctLawsuitData = createAsyncThunk<
  void,
  { data: LawsuitCorrectionFormValues; lawsuitId: number },
  { rejectValue: BatchApiErrorElement[] }
>(
  `${MODULE_NAME}/correctLawsuitData`,
  async ({ lawsuitId, data }, { rejectWithValue, getState }) => {
    const state = getState() as RootState
    const files = getDataCorrectionFiles(state)
    const { files: lawsuitFiles } = getFilesAndAnswersToCorrect(state)

    const formData = new FormData()

    const ANSWERS_KEY = 'answers'
    const FILES_KEY = 'files'

    formData.append('id', `${lawsuitId}`)

    Object.entries(files).forEach(([fileId, file], idx) => {
      const fileType =
        lawsuitFiles.find(item => `${item.id}` === fileId)?.type || 1
      formData.append(`${FILES_KEY}.index`, `${idx}`)
      formData.append(`${FILES_KEY}[${idx}].id`, `${fileId}`)
      formData.append(`${FILES_KEY}[${idx}].file`, file)
      formData.append(`${FILES_KEY}[${idx}].type`, `${fileType}`)
    })

    Object.entries(data).forEach(([key, value], idx) => {
      if (key === 'message') {
        formData.append('userRemarks', value)
      } else {
        formData.append(`${ANSWERS_KEY}.index`, `${idx}`)
        formData.append(`${ANSWERS_KEY}[${idx}].id`, `${key}`)
        formData.append(`${ANSWERS_KEY}[${idx}].content`, `${value}`)
      }
    })

    try {
      await Api.put(`${paths.Lawsuits.root}/${lawsuitId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (error) {
      return rejectWithValue(Array.isArray(error) ? error : [])
    }
  }
)

export const setBankTransferInfo = createAsyncThunk<
  void,
  SetBankTransferInfoPayload,
  { rejectValue: string }
>(`${MODULE_NAME}/setBankTransferInfo`, async (data, { rejectWithValue }) => {
  try {
    await Api.put(`${paths.Lawsuits.root}/${data.id}/bank-transfers`, data)
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const getBankTransfers = createAsyncThunk<
  BankTransfer[],
  string | number,
  { rejectValue: string }
>(`${MODULE_NAME}/getBankTransfers`, async (lawsuitId, { rejectWithValue }) => {
  try {
    const response = await Api.get(
      `${paths.Lawsuits.root}/${lawsuitId}/bank-transfers`
    )
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const uploadBankTransferConfirmation = createAsyncThunk<
  void,
  { lawsuitId: string | number; files: File[] },
  { rejectValue: string }
>(
  `${MODULE_NAME}/uploadBankTransferConfirmation`,
  async ({ lawsuitId, files }, thunkAPI) => {
    try {
      const formData = new FormData()
      formData.append('id', `${lawsuitId}`)
      files.forEach(file => {
        formData.append('files', file)
      })

      await Api.put(
        `${paths.Lawsuits.root}/${lawsuitId}/bank-transfers/files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const getBankTransferFiles = createAsyncThunk<
  { id: number; name: string }[],
  string | number,
  { rejectValue: string }
>(
  `${MODULE_NAME}/getBankTransferFiles`,
  async (lawsuitId, { rejectWithValue }) => {
    try {
      const response = await Api.get(
        `${paths.Lawsuits.root}/${lawsuitId}/bank-transfers/files`
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const verifyBankTransferFiles = createAsyncThunk<
  void,
  { id: string | number; filesValid: boolean; remarks?: string },
  { rejectValue: string }
>(
  `${MODULE_NAME}/verifyBankTransferFiles`,
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      await Api.put(
        `${paths.Lawsuits.root}/${id}/bank-transfers/files/assess`,
        { id, ...data }
      )
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getLawsuitHyperlinks = createAsyncThunk<
  LawsuitLinks,
  void,
  { rejectValue: string }
>(`${MODULE_NAME}/getLawsuitHyperlinks`, async (_, { rejectWithValue }) => {
  try {
    const response = await Api.get(paths.Lawsuits.hyperlinks)
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const updateActSignature = createAsyncThunk<
  void,
  {
    actSignature: string
    lawsuitId: string
  },
  { rejectValue: string }
>(
  `${MODULE_NAME}/updateActSignature`,
  async ({ actSignature, lawsuitId }, { rejectWithValue }) => {
    try {
      const response = await Api.put(
        `${paths.Lawsuits.root}/${lawsuitId}/act-signature`,
        {
          id: lawsuitId,
          actSignature,
        }
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const uploadFiledLawsuitFiles = createAsyncThunk<
  void,
  { message: string; lawsuitId: string },
  { rejectValue: BatchApiErrorElement[] }
>(
  `${MODULE_NAME}/uploadFiledLawsuitFiles`,
  async ({ lawsuitId, message }, { rejectWithValue, getState }) => {
    const state = getState() as RootState
    const files = getFiledLawsuitFiles(state)
    const formData = new FormData()

    formData.append('id', lawsuitId)
    formData.append('message', message)

    files.forEach(file => {
      formData.append('files', file)
    })

    try {
      await Api.put(`${paths.Lawsuits.root}/${lawsuitId}/file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (error) {
      return rejectWithValue(Array.isArray(error) ? error : [])
    }
  }
)

export const updateLawsuit = createAsyncThunk<
  void,
  {
    id: string
    title: string
    content: string
    actionRequired: boolean
    lawsuitStatus: string | number
    paymentRequired: boolean
    bankTransfer?: {
      recipient: string
      bankAccountNumber: string
      title: string
      amount: string | number
      currencyId: string | number
    }
  },
  { rejectValue: BatchApiErrorElement[] }
>(
  `${MODULE_NAME}/updateLawsuit`,
  async ({ bankTransfer, ...data }, { rejectWithValue, getState }) => {
    const state = getState() as RootState
    const files = getUpdateLawsuitFiles(state)
    const formData = new FormData()

    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, `${value}`)
    })

    if (data.paymentRequired && bankTransfer) {
      Object.entries(bankTransfer).forEach(([key, value]) => {
        formData.append(`bankTransfer.${key}`, `${value}`)
      })
    }

    files.forEach(file => {
      formData.append('files', file)
    })

    try {
      await Api.put(`${paths.Lawsuits.root}/${data.id}/status`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (error) {
      return rejectWithValue(Array.isArray(error) ? error : [])
    }
  }
)

export const sendMessage = createAsyncThunk<
  void,
  { message: string; lawsuitId: string },
  { rejectValue: BatchApiErrorElement[] }
>(
  `${MODULE_NAME}/sendMessage`,
  async ({ lawsuitId, message }, { rejectWithValue, getState }) => {
    const state = getState() as RootState
    const files = getSendMessageFiles(state)
    const formData = new FormData()

    formData.append('id', lawsuitId)
    formData.append('message', message)
    formData.append('lawsuitScopeChanged', 'false')

    files.forEach(file => {
      formData.append('files', file)
    })

    try {
      await Api.post(`${paths.Lawsuits.root}/${lawsuitId}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (error) {
      return rejectWithValue(Array.isArray(error) ? error : [])
    }
  }
)

export const markLawsuitMessagesAsRead = createAsyncThunk<
  void,
  {
    lawsuitId: string
  },
  { rejectValue: string }
>(
  `${MODULE_NAME}/markLawsuitMessagesAsRead`,
  async ({ lawsuitId }, { rejectWithValue }) => {
    try {
      const response = await Api.put(
        `${paths.Lawsuits.root}/${lawsuitId}/messages/read`
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const withdrawLawsuit = createAsyncThunk<
  void,
  {
    lawsuitId: string
    invoicePaid: boolean
  },
  { rejectValue: string }
>(
  `${MODULE_NAME}/withdrawLawsuit`,
  async ({ lawsuitId: id, invoicePaid }, { rejectWithValue }) => {
    try {
      await Api.put(`${paths.Lawsuits.root}/${id}/cancel`, {
        id,
        invoicePaid,
      })
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const sendLawsuitWithdrawalMessage = createAsyncThunk<
  void,
  {
    lawsuitId: string
    message: string
  },
  { rejectValue: string }
>(
  `${MODULE_NAME}/sendLawsuitWithdrawalMessage`,
  async ({ lawsuitId, message }, { rejectWithValue, getState }) => {
    const state = getState() as RootState
    const files = getSendMessageFiles(state)
    const formData = new FormData()

    formData.append('id', lawsuitId)
    formData.append('message', message)
    formData.append('lawsuitScopeChanged', 'true')

    files.forEach(file => {
      formData.append('files', file)
    })

    try {
      await Api.post(`${paths.Lawsuits.root}/${lawsuitId}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const withdrawPowerOfAttorney = createAsyncThunk<
  void,
  undefined,
  { rejectValue: { error: string; errorCode: string } }
>(`${MODULE_NAME}/withdrawPowerOfAttorney`, async (_, thunkAPI) => {
  try {
    await Api.post(`${paths.Users.withdrawPowerOfAttorney}`)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getPowerOfAttorneyPreview = createAsyncThunk<
  void,
  undefined,
  { rejectValue: { error: string; errorCode: string } }
>(`${MODULE_NAME}/getPowerOfAttorneyPreview`, async (_, thunkAPI) => {
  try {
    const response = await Api.get(`${paths.Users.powerOfAttorney}`, {
      responseType: 'blob',
    })
    const blob = response.data
    const href = window.URL.createObjectURL(blob)
    window.open(href, '_blank')
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})
